import { fromPath } from '../../gardener/sets/mythic';
import { type PixelCanvas, sprite } from '../../lib';
import { MYTHIC_FX } from './mythic';
import { burst, frames3, frames4, jagged, muzzle, paint, shot, type Tone, under } from './mythic/shared';

/**
 * The mythic guns' shots (ADR-0160), as an effect skin's (ADR-0113): for each a bullet at the stock pellet's size (its
 * head right, turned along its flight, on the pellet's glow tinted the skin's), a muzzle flash pointing right (origin
 * left-middle, 3 frames) and the burst where a shot ends (4 frames). Outline-free: effects are the brightest things on
 * screen. No team keys: a shot isn't recoloured by team.
 */

// --- Hellfire --------------------------------------------------------------------------------------------------------

const fireball = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const head = under(Math.hypot(x - 21, y - 8), [
      ['w', 1.8],
      ['y', 3.1],
      ['Y', 4.4],
      ['o', 5.2],
    ]);
    if (head && (x >= 20 || Math.hypot(x - 21, y - 8) < 4.4)) return head;
    if (x >= 21) return null;
    // Its tail: fire streaming back, wavering, breaking up toward its end.
    const t = (21 - x) / 19;
    if (t > 1) return null;
    const half = 4.6 * (1 - t) ** 0.8 + Math.sin(t * 9 + f * 2.1) * 0.9 * t;
    const d = Math.abs(y - 8 - Math.sin(t * 7 + f * 1.7) * 1.3 * t);
    if (d >= half) return null;
    return d < half * 0.35 && t < 0.5 ? 'y' : d < half * 0.7 && t < 0.8 ? 'Y' : t > 0.75 ? 'u' : 'o';
  });

const fireJet = (f: number) => (c: PixelCanvas) => {
  const k = [1, 0.72, 0.42][f] as number;
  paint(c, (x, y) => {
    const t = (x - 4) / (40 * k);
    if (t < 0 || t > 1) return null;
    const shape = t < 0.3 ? (t / 0.3) ** 0.7 : (1 - (t - 0.3) / 0.7) ** 0.85;
    const half = 12.5 * k * shape + Math.sin(t * 11 + f * 2.3) * 1.4 * t;
    const d = Math.abs(y - 20 - Math.sin(t * 6 + f) * 1.6 * t) / Math.max(0.4, half);
    if (d >= 1) return null;
    // Breaking up as it dies.
    if (f === 2 && (Math.floor(x) * 3 + Math.floor(y) * 5) % 7 < 2) return null;
    return d < 0.28 && t < 0.6 && f < 2 ? 'w' : d < 0.52 ? 'y' : d < 0.78 ? 'Y' : t > 0.8 ? 'u' : 'o';
  });
};

const fireBurst = (f: number) => (c: PixelCanvas) => {
  const R = [11, 14.5, 16.5, 18][f] as number;
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const a = Math.atan2(dy, dx);
    const r = Math.hypot(dx, dy) / (R * (1 + 0.16 * Math.sin(5 * a + f * 1.3) + 0.09 * Math.sin(9 * a - f)));
    if (r >= 1) return null;
    if (f === 0)
      return under(r, [
        ['w', 0.3],
        ['y', 0.55],
        ['Y', 0.8],
        ['o', 1],
      ]);
    if (f === 1) return r < 0.42 ? null : r < 0.6 ? 'y' : r < 0.82 ? 'Y' : 'o';
    if (f === 2) return r > 0.72 && Math.sin(3 * a + 1) > -0.25 ? (r > 0.88 ? 'u' : 'o') : null;
    // The last embers.
    return r > 0.8 && Math.sin(7 * a + 2) > 0.86 ? (r > 0.9 ? 'u' : 'o') : null;
  });
};

// --- Foxfire ---------------------------------------------------------------------------------------------------------

const wisp = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const head = under(Math.hypot(x - 21, y - 8), [
      ['w', 1.7],
      ['i', 3.1],
      ['p', 4.3],
    ]);
    if (head) return head;
    if (x >= 21) return null;
    // A spirit's tail: curling as it goes, thinning to nothing.
    const t = (21 - x) / 18;
    if (t > 1) return null;
    const half = 3.7 * (1 - t) ** 1.2;
    const d = Math.abs(y - 8 - Math.sin(t * 6 + f * 2.1) * 2.6 * t);
    if (d >= half) return null;
    return d < half * 0.45 && t < 0.35 ? 'w' : t > 0.7 ? 'P' : d < half * 0.7 ? 'i' : 'p';
  });

/** A petal at (px, py), `rx` × `ry`, turned by `a`: pink, a white light on it, deeper at its tip. */
const petal = (x: number, y: number, px: number, py: number, rx: number, ry: number, a: number): Tone => {
  const [dx, dy] = [x - px, y - py];
  const [u, v] = [(dx * Math.cos(a) + dy * Math.sin(a)) / rx, (-dx * Math.sin(a) + dy * Math.cos(a)) / ry];
  const r = u * u + v * v;
  if (r >= 1) return null;
  return u < -0.25 && v < 0.2 && r < 0.55 ? 'w' : u > 0.5 ? 'p' : 'i';
};

const petalPuff = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    if (f === 0) {
      const flash = under(Math.hypot(x - 7, y - 20), [
        ['w', 3],
        ['i', 5],
        ['p', 6.2],
      ]);
      if (flash) return flash;
    }
    const size = [1, 0.8, 0.55][f] as number;
    for (const [i, a] of [-0.75, -0.38, -0.05, 0.3, 0.66, 0.12].entries()) {
      const d = 9 + f * 9 + (i % 3) * 3.5;
      const p = petal(x, y, 6 + Math.cos(a) * d, 20 + Math.sin(a) * d, 3.4 * size, 2 * size, a + i * 0.9);
      if (p) return p;
    }
    return null;
  });

const blossom = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    // Five petals round a gold heart, opening, then blown apart.
    if (f === 0 && Math.hypot(x - 22, y - 22) < 2.3) return 'y';
    const d = [6.5, 10, 14, 17.5][f] as number;
    const [rx, ry] = [
      [5.4, 3.5],
      [4.2, 2.7],
      [3, 2],
      [1.7, 1.3],
    ][f] as [number, number];
    for (let k = 0; k < 5; k++) {
      const a = (k * Math.PI * 2) / 5 - Math.PI / 2 + f * 0.22;
      const p = petal(x, y, 22 + Math.cos(a) * d, 22 + Math.sin(a) * d, rx, ry, a);
      if (p) return f === 3 ? 'p' : p;
    }
    return null;
  });

// --- Singularity -----------------------------------------------------------------------------------------------------

/** Where (x, y) is on a ring round (cx, cy): how far off its line (its own units) and its near side. */
const ringAt = (x: number, y: number, cx: number, cy: number, rx: number, ry: number, tilt: number) => {
  const [dx, dy] = [x - cx, y - cy];
  const [u, v] = [(dx * Math.cos(tilt) + dy * Math.sin(tilt)) / rx, (-dx * Math.sin(tilt) + dy * Math.cos(tilt)) / ry];
  return { off: Math.hypot(u, v) - 1, near: v > 0, at: Math.atan2(v, u) };
};

const blackHole = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const d = Math.hypot(x - 21, y - 8);
    const ring = ringAt(x, y, 21, 8.3, 8.6, 2.5, -0.2);
    if (Math.abs(ring.off) < 0.16 && (ring.near || d > 3.9)) {
      // A bead of light running round the ring.
      const bead = Math.abs(((ring.at - f * 2.1 + Math.PI * 3) % (Math.PI * 2)) - Math.PI) < 0.45;
      return bead ? 'w' : 'i';
    }
    if (d < 3.9) return d > 2.9 ? (x + y < 28.5 ? 'i' : 'p') : 'k';
    // What it has drunk, strung out behind it.
    if (y > 7 && y < 9) {
      if (Math.abs(x - 11 - (f % 2)) < 1) return 'p';
      if (Math.abs(x - 6.5 + (f % 2)) < 0.5) return 'P';
    }
    return null;
  });

const collapse = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [cx, rx, ry, hole] = [
      [10, 5, 13, 4],
      [17, 4, 9, 2.6],
      [23, 2.2, 5, 0],
    ][f] as [number, number, number, number];
    const d = Math.hypot(x - cx, y - 20);
    if (hole && d < hole) return d > hole - 1.1 ? 'p' : 'k';
    const off = Math.abs(Math.hypot((x - cx) / rx, (y - 20) / ry) - 1);
    if (off < 0.16) return f === 2 ? 'P' : Math.abs(y - 20) < 2.5 ? 'w' : f === 0 ? 'i' : 'p';
    if (f === 2 && d < 1.4) return 'w';
    return null;
  });

const implosion = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 3) {
      // It pops: a star of light where it was.
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 1.6) return 'w';
      if ((Math.abs(dx) < 0.6 && Math.abs(dy) < 10) || (Math.abs(dy) < 0.6 && Math.abs(dx) < 10))
        return d < 6 ? 'w' : 'i';
      return Math.abs(Math.abs(dx) - Math.abs(dy)) < 0.75 && d < 6 ? 'p' : null;
    }
    const [ring, hole] = [
      [15.5, 7],
      [10.5, 5],
      [5.8, 2.6],
    ][f] as [number, number];
    if (d < hole) return d > hole - 1.2 ? (dx + dy < 0 ? 'i' : 'p') : 'k';
    if (Math.abs(d - ring) < (f === 0 ? 0.8 : 1.1)) return f === 0 ? 'P' : f === 1 ? 'p' : 'i';
    // Stars pulled in with it.
    const a = Math.atan2(dy, dx);
    return f < 2 && Math.abs(d - ring * 0.72) < 0.9 && Math.sin(a * 4 + f * 0.8) > 0.93 ? 'w' : null;
  });

// --- Thunderclap -----------------------------------------------------------------------------------------------------

/** A bolt's light: white along its line, yellow round it. */
const lit = (d: number): Tone => (d < 0.7 ? 'w' : d < 1.9 ? 'y' : null);

const ZIGZAGS = [
  [-3, 2, -2, 3, -1],
  [2, -3, 3, -2, 1],
  [-2, 3, -3, 1, -1],
] as const;

const bolt = (f: number) => (c: PixelCanvas) => {
  const path = jagged(2, 8, 28, 8, ZIGZAGS[f] ?? ZIGZAGS[0]);
  paint(c, (x, y) =>
    Math.hypot(x - 28.5, y - 8) < 2.4 ? 'w' : Math.hypot(x - 28.5, y - 8) < 3.5 ? 'y' : lit(fromPath(x, y, path)),
  );
};

const fork = (f: number) => (c: PixelCanvas) => {
  const k = [1, 0.75, 0.5][f] as number;
  const forks = [
    jagged(5, 20, 5 + 38 * k, 20, [3 - f, -4, 3, -2 + f]),
    jagged(8, 19, 8 + 24 * k, 20 - 14 * k, [-2, 3 - f, -2]),
    jagged(8, 21, 8 + 22 * k, 20 + 14 * k, [2, -3 + f, 2]),
  ];
  paint(c, (x, y) => {
    if (f === 0) {
      const flash = under(Math.hypot(x - 6, y - 20), [
        ['w', 3.2],
        ['y', 6],
      ]);
      if (flash) return flash;
    }
    // Breaking up as it dies: its middle gone.
    if (f === 2 && x > 12 && x < 18) return null;
    return lit(Math.min(...forks.map((p) => fromPath(x, y, p))));
  });
};

const crackle = (f: number) => (c: PixelCanvas) => {
  const [from, to] = [
    [0, 17],
    [5, 19],
    [10, 20],
    [17, 21],
  ][f] as [number, number];
  const arcs = Array.from({ length: 6 }, (_, k) => {
    const a = (k * Math.PI) / 3 + 0.26 + f * 0.2;
    const [ux, uy] = [Math.cos(a), Math.sin(a)];
    return jagged(
      22 + ux * from,
      22 + uy * from,
      22 + ux * to,
      22 + uy * to,
      f === 3 ? [] : [k % 2 ? 3 : -3, k % 2 ? -3 : 3, k % 3 ? 2 : -2],
    );
  });
  paint(c, (x, y) => {
    if (f === 0) {
      const flash = under(Math.hypot(x - 22, y - 22), [
        ['w', 3.4],
        ['y', 5.6],
      ]);
      if (flash) return flash;
    }
    const d = Math.min(...arcs.filter((_, k) => f < 2 || k % 2 === 0).map((p) => fromPath(x, y, p)));
    return f === 3 ? (d < 0.9 ? 'y' : null) : lit(d);
  });
};

export default [
  sprite({
    name: 'hellfire_shot',
    ...shot,
    notes: `Hellfire's bullet (ADR-0160): a ball of fire, white-hot at its heart (the stock pellet's size), its tail streaming
back and breaking up. Head right. Rubric (4×): S5 R5 P5 L4 C4 A4 F4. Weakest: on the lane's brown its rust tail goes.`,
    anims: { idle: { fps: 15, frames: frames3(fireball) } },
  }),
  sprite({
    name: 'hellfire_muzzle',
    ...muzzle,
    notes: `Hellfire's muzzle flash (ADR-0160): a jet of fire out of the muzzle, fat a third of the way out, white at its
heart, dying back and breaking up over 3 frames. Points right. Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(fireJet) } },
  }),
  sprite({
    name: 'hellfire_burst',
    ...burst,
    notes: `Hellfire's hit (ADR-0160): a burst of fire, a ragged white-hot ball, then a ring of flame opening, breaking into
arcs, a few embers last. 4 frames. Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(fireBurst) } },
  }),
  sprite({
    name: 'foxfire_shot',
    ...shot,
    notes: `Foxfire's bullet (ADR-0160): a spirit light, a white heart in pink, its tail curling behind it and thinning to
nothing. Head right. Rubric (4×): S5 R4 P5 L4 C4 A4 F4. Weakest: pink on the lane is low in contrast (its glow carries it).`,
    anims: { idle: { fps: 12, frames: frames3(wisp) } },
  }),
  sprite({
    name: 'foxfire_muzzle',
    ...muzzle,
    notes: `Foxfire's muzzle flash (ADR-0160): a puff of cherry petals blown out of the muzzle from a pink-white flash, flying
on and shrinking over 3 frames. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(petalPuff) } },
  }),
  sprite({
    name: 'foxfire_burst',
    ...burst,
    notes: `Foxfire's hit (ADR-0160): a cherry blossom opening (five petals round a gold heart), its petals blown apart and
gone. 4 frames. Rubric (4×): S5 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(blossom) } },
  }),
  sprite({
    name: 'singularity_shot',
    ...shot,
    notes: `Singularity's bullet (ADR-0160): a black star (the stock pellet's size) in a violet rim, a ring round it with a bead
of light running along, what it drank strung out behind. Head right. Rubric (4×): S5 R4 P5 L4 C4 A4 F4. Weakest: its
black heart needs its glow under it on dark ground.`,
    anims: { idle: { fps: 14, frames: frames3(blackHole) } },
  }),
  sprite({
    name: 'singularity_muzzle',
    ...muzzle,
    notes: `Singularity's muzzle flash (ADR-0160): space folding at the muzzle, a ring of light round a black hole, flying on
and closing to a point over 3 frames. Points right. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(collapse) } },
  }),
  sprite({
    name: 'singularity_burst',
    ...burst,
    notes: `Singularity's hit (ADR-0160): it collapses, a ring of light falling in on a black hole with the stars round it, then
pops into a star of white light. 4 frames. Rubric (4×): S5 R4 P5 L4 C4 A5 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(implosion) } },
  }),
  sprite({
    name: 'thunderclap_shot',
    ...shot,
    notes: `Thunderclap's bullet (ADR-0160): a bolt of lightning, white along its jagged line in yellow light, a white-hot
head (the stock pellet's size), a new zigzag each frame. Head right. Rubric (4×): S5 R5 P5 L4 C4 A4 F4.`,
    anims: { idle: { fps: 18, frames: frames3(bolt) } },
  }),
  sprite({
    name: 'thunderclap_muzzle',
    ...muzzle,
    notes: `Thunderclap's muzzle flash (ADR-0160): lightning forking out of the muzzle three ways from a white flash, shorter
and broken over 3 frames. Points right. Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(fork) } },
  }),
  sprite({
    name: 'thunderclap_burst',
    ...burst,
    notes: `Thunderclap's hit (ADR-0160): a crackle, six jagged arcs out of a white flash, flung out and thinning to sparks.
4 frames. Rubric (4×): S4 R5 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(crackle) } },
  }),
  // The mythic sets after the first four (ADR-0160 amended): a file a set in `mythic/`.
  ...MYTHIC_FX,
];
